export function deletedSourceId(id, title = 'Source file') {
  return `deleted-source:${encodeURIComponent(id)}:${encodeURIComponent(title)}`;
}

export function deletedSourceFromId(id) {
  if (typeof id !== 'string' || !id.startsWith('deleted-source:')) return null;
  const [, originalId = '', title = ''] = id.split(':');
  try {
    return { id, originalId: decodeURIComponent(originalId), title: decodeURIComponent(title) || 'Source file', type: 'DeletedSource' };
  } catch {
    return { id, originalId, title: 'Source file', type: 'DeletedSource' };
  }
}

export function reportSourceIds(report) {
  const documents = report.documents ?? report.originalData?.documents;
  const consultationId = report.consultationId ?? report.originalData?.consultationId;
  return Array.isArray(documents) && documents.length ? documents : consultationId ? [consultationId] : [];
}

export function resolveReportSources(ids, history, { currentConsultation, patientId } = {}) {
  // A just-saved transcript can be opened in Write before the library catches
  // up. Only use the current consultation belonging to this patient.
  const currentSource = patientId && currentConsultation?.patient_id === patientId && currentConsultation.id
    ? { id: currentConsultation.id, title: currentConsultation.chiefComplaint || 'Consultation',
        type: 'Consultation', originalData: currentConsultation }
    : null;
  // Absence from the current list is not proof of deletion. Keep the original
  // ID so a later successful history load can resolve the real source again.
  return ids.map(id => history.find(item => item.id === id)
    || (currentSource?.id === id ? currentSource : null)
    || deletedSourceFromId(id) || { id, title: 'Unavailable source', type: 'UnavailableSource' });
}

export function hasUnavailableReportSources(sources) {
  return sources.some(source => source.type === 'DeletedSource' || source.type === 'UnavailableSource');
}

export async function findReferencingReports(client, patientId, ids) {
  const { data, error } = await client.from('generatedreports')
    .select('id,title,documents,consultationId').eq('patientId', patientId);
  if (error) throw error;
  const reports = (data || []).filter(report => !ids.includes(report.id)
    && reportSourceIds(report).some(id => ids.includes(id)));
  return Promise.all(reports.map(async report => {
    let title = 'Untitled report';
    if (report.title) {
      try {
        const result = await client.rpc('decrypt_text', { encrypted_data: report.title });
        // The RPC returns the input when decryption fails. Never display
        // that ciphertext as a report name in confirmation or error dialogs.
        if (!result.error && typeof result.data === 'string' && result.data.trim()
          && !/^\\x[0-9a-f]+$/i.test(result.data)) title = result.data;
      } catch { /* Keep a readable name when the decryption request fails. */ }
    }
    return { ...report, title };
  }));
}

// The database locks each report and marks sources in its latest committed list.
// Long deletion markers stay in the POST body, never in URL equality filters.
export async function markDeletedReportSources(client, patientId, deletedItems, onError) {
  const titles = new Map(deletedItems.map(item => [item.id, item.title]));
  const deletedSources = [...titles].map(([id, title]) => ({ id, marker: deletedSourceId(id, title) }));
  const reports = await findReferencingReports(client, patientId, [...titles.keys()]);
  const selections = new Map();
  for (const report of reports) {
    // The source was already deleted. Keep the open report visibly unavailable
    // even when persisting its marker fails; the caller also displays a warning.
    selections.set(report.id, reportSourceIds(report).map(id => titles.has(id) ? deletedSourceId(id, titles.get(id)) : id));
    try {
      const { data, error } = await client.rpc('mark_deleted_report_sources', {
        p_patient_id: patientId, p_report_id: report.id, p_deleted_sources: deletedSources,
      });
      if (error) throw error;
      if (data?.status === 'not_found') {
        selections.delete(report.id);
      } else if (['saved', 'already_saved'].includes(data?.status)
        && Array.isArray(data.documents) && data.documents.every(id => typeof id === 'string')) {
        // The RPC may include sources another editor added after our read.
        selections.set(report.id, data.documents);
      } else {
        throw new Error('Could not confirm the updated report sources.');
      }
    } catch (error) { onError?.(report, error); }
  }
  return selections;
}
